import { useState, useEffect, useCallback, useRef } from "react";
import { isAxiosError } from "axios";
import {
  getLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  setArticleLabel,
  getArticleLabels,
  GrantsLabelItem,
  ArticleLabelMap,
} from "../../../services/grantsApi";
import { LABEL_COLORS } from "./labelColors";

/**
 * Maximum number of labels allowed per tenant.
 */
export const MAX_LABELS = 10;

/**
 * Whitelist of backend error messages safe to display to the user.
 */
const SAFE_ERROR_MESSAGES = [
  "Osiagnięto limit 10 etykiet.",
  "Osiągnięto limit 10 etykiet.",
  "Etykieta o tej nazwie już istnieje.",
  "Etykieta nie została znaleziona.",
  "Nieprawidłowy kolor.",
];

/**
 * Gets a user-friendly error message from an API error.
 */
export function getSafeErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const backendMsg = err.response?.data?.message;
    if (
      typeof backendMsg === "string" &&
      SAFE_ERROR_MESSAGES.includes(backendMsg)
    ) {
      return backendMsg;
    }
  }
  return "Wystąpił błąd. Spróbuj ponownie.";
}

/**
 * Picks the first color from the LABEL_COLORS palette not used by any existing label.
 * Falls back to the first color if all are used.
 */
export function getAutoColor(labels: GrantsLabelItem[]): string {
  const usedColors = new Set(labels.map((l) => l.color));
  const firstUnused = LABEL_COLORS.find((c) => !usedColors.has(c.key));
  return firstUnused?.key ?? LABEL_COLORS[0].key;
}

interface UseCallLabelsOptions {
  /** IDs of calls currently visible on the page (for fetching assignments) */
  callIds: string[];
}

/**
 * Hook for managing labels on grant calls (articleType = 'call').
 *
 * Provides label CRUD, call-label assignments, and auto-color selection.
 * Designed for the Calls table where each row can have a label assigned.
 */
export function useCallLabels({ callIds }: UseCallLabelsOptions) {
  const [labels, setLabels] = useState<GrantsLabelItem[]>([]);
  const [articleLabels, setArticleLabels] = useState<ArticleLabelMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stabilize callIds by comparing contents, not reference
  const callIdsKey = callIds.join(",");
  const callIdsRef = useRef(callIds);
  if (callIdsKey !== callIdsRef.current.join(",")) {
    callIdsRef.current = callIds;
  }
  const stableCallIds = callIdsRef.current;

  // Fetch tenant labels
  const fetchLabels = useCallback(async () => {
    try {
      const data = await getLabels();
      setLabels(data);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.response?.data?.message || "Błąd pobierania etykiet");
      } else {
        setError("Błąd pobierania etykiet");
      }
    }
  }, []);

  // Fetch article-label assignments for current page of calls
  const fetchArticleLabels = useCallback(async () => {
    if (stableCallIds.length === 0) {
      setArticleLabels({});
      return;
    }
    try {
      const data = await getArticleLabels("call", stableCallIds);
      setArticleLabels(data);
    } catch (err: unknown) {
      // Non-critical - label display degraded gracefully
      console.error("Failed to fetch call labels:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callIdsKey]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLabels(), fetchArticleLabels()]).finally(() =>
      setLoading(false)
    );
  }, [fetchLabels, fetchArticleLabels]);

  const handleCreateLabel = useCallback(
    async (name: string, color: string) => {
      const created = await createLabel({ name, color });
      setLabels((prev) => [...prev, created]);
      return created;
    },
    []
  );

  /**
   * Creates a new label with auto-color and immediately assigns it to a call.
   * Used by the inline "Nowa etykieta..." flow in the dropdown.
   */
  const handleCreateAndAssign = useCallback(
    async (name: string, callId: string) => {
      const autoColor = getAutoColor(labels);
      const created = await createLabel({ name, color: autoColor });
      setLabels((prev) => [...prev, created]);

      // Assign the new label to the call
      await setArticleLabel("call", callId, created.id);
      setArticleLabels((prev) => ({
        ...prev,
        [callId]: {
          labelId: created.id,
          labelName: created.name,
          color: created.color,
        },
      }));

      return created;
    },
    [labels]
  );

  const handleUpdateLabel = useCallback(
    async (
      id: string,
      payload: { name?: string; color?: string; sortOrder?: number }
    ) => {
      const updated = await updateLabel(id, payload);
      setLabels((prev) => prev.map((l) => (l.id === id ? updated : l)));
      // Update articleLabels that reference this label
      setArticleLabels((prev) => {
        const next = { ...prev };
        for (const [artId, info] of Object.entries(next)) {
          if (info.labelId === id) {
            next[artId] = {
              ...info,
              labelName: updated.name,
              color: updated.color,
            };
          }
        }
        return next;
      });
      return updated;
    },
    []
  );

  const handleDeleteLabel = useCallback(async (id: string) => {
    await deleteLabel(id);
    setLabels((prev) => prev.filter((l) => l.id !== id));
    // Remove article-label assignments for this label (CASCADE on backend)
    setArticleLabels((prev) => {
      const next = { ...prev };
      for (const [artId, info] of Object.entries(next)) {
        if (info.labelId === id) {
          delete next[artId];
        }
      }
      return next;
    });
  }, []);

  const handleSetCallLabel = useCallback(
    async (callId: string, labelId: string | null) => {
      const result = await setArticleLabel("call", callId, labelId);
      setArticleLabels((prev) => {
        const next = { ...prev };
        if (result.labelId === null) {
          delete next[callId];
        } else {
          const label = labels.find((l) => l.id === result.labelId);
          if (label) {
            next[callId] = {
              labelId: label.id,
              labelName: label.name,
              color: label.color,
            };
          }
        }
        return next;
      });
      return result;
    },
    [labels]
  );

  return {
    labels,
    articleLabels,
    loading,
    error,
    createLabel: handleCreateLabel,
    createAndAssign: handleCreateAndAssign,
    updateLabel: handleUpdateLabel,
    deleteLabel: handleDeleteLabel,
    setCallLabel: handleSetCallLabel,
    refetchLabels: fetchLabels,
    refetchArticleLabels: fetchArticleLabels,
  };
}
